import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { motion, AnimatePresence } from 'framer-motion';
import toast from 'react-hot-toast';
import { ArrowRight, ArrowLeft, Loader2, Sparkles, Check } from 'lucide-react';
import { api } from '../lib/api';
import { useAuth } from '../lib/store';
import { AuthLayout } from './Login';
import CountryCityPicker from '../components/CountryCityPicker';
import CharacterSelect from '../components/CharacterSelect';
import { getTimezone } from '../lib/countries';

export default function AuthSetup() {
  const [sp] = useSearchParams();
  const nav = useNavigate();
  const setToken = useAuth(s => s.setToken);
  const setupToken = sp.get('t');

  const [step, setStep] = useState(1);
  const [loc, setLoc] = useState({ timezone: getTimezone() });
  const [classKey, setClassKey] = useState(null);
  const [templateId, setTemplateId] = useState(null);

  useEffect(() => {
    if (!setupToken) { toast.error('Invalid setup link'); nav('/join'); }
  }, [setupToken, nav]);

  const complete = useMutation({
    mutationFn: () => api.post('/auth/oauth/complete', {
      setupToken, templateId,
      cityGroupId: loc.cityGroupId, countryGroupId: loc.countryGroupId,
      countryCode: loc.countryCode, timezone: loc.timezone,
    }),
    onSuccess: async ({ data }) => {
      await setToken(data.token);
      toast.success(`Welcome, ${data.hero?.name || data.user.username}! ⚔️`);
      nav('/hero', { replace: true });
    },
    onError: e => toast.error(e.message),
  });

  return (
    <AuthLayout
      title={step === 1 ? 'Where are you?' : 'Choose your hero'}
      sub={step === 1 ? 'Join your city community' : 'Pick a class and a champion'}
      wide={step === 2}
    >
      <div className="flex items-center gap-1.5 mb-5">
        {[1, 2].map((n, i) => (
          <div key={n} className="flex items-center flex-1">
            <div className={`w-7 h-7 rounded-full grid place-items-center text-[11px] font-black
              ${step > n ? 'bg-mint-500 text-white'
                : step === n ? 'bg-brand-500 text-white' : 'bg-ink-100 text-ink-400'}`}>
              {step > n ? <Check size={13} strokeWidth={3} /> : n}
            </div>
            {i === 0 && <div className={`flex-1 h-0.5 mx-1.5 ${step > 1 ? 'bg-mint-500' : 'bg-ink-100'}`} />}
          </div>
        ))}
      </div>

      <AnimatePresence mode="wait">
        {step === 1 ? (
          <motion.div key="a" initial={{ opacity: 0, x: -18 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: 18 }}>
            <CountryCityPicker value={loc} onChange={setLoc} />
          </motion.div>
        ) : (
          <motion.div key="b" initial={{ opacity: 0, x: -18 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: 18 }}>
            <CharacterSelect classKey={classKey} templateId={templateId}
              onClass={setClassKey} onTemplate={setTemplateId} />
          </motion.div>
        )}
      </AnimatePresence>

      <div className="flex gap-2 mt-6">
        {step > 1 && (
          <button onClick={() => setStep(1)} className="btn-ghost"><ArrowLeft size={15} /> Back</button>
        )}
        {step === 1 ? (
          <button onClick={() => loc.cityGroupId && setStep(2)} disabled={!loc.cityGroupId}
            className="btn-primary flex-1 py-3">Continue <ArrowRight size={15} /></button>
        ) : (
          <button onClick={() => complete.mutate()} disabled={!templateId || complete.isPending}
            className="btn-primary flex-1 py-3 font-black">
            {complete.isPending
              ? <><Loader2 size={16} className="animate-spin" /> Creating…</>
              : <><Sparkles size={16} /> Start playing</>}
          </button>
        )}
      </div>
    </AuthLayout>
  );
}